// Утилиты для безопасного форматирования с правильной локализацией

/**
 * [NOTIF-2026-05-22] Безопасный парсинг datetime из бэкенда. Бэк часто отдаёт
 * смесь форматов: новые записи с UTC-офсетом (`...Z` или `+00:00`), легаси —
 * naive ISO без офсета (исторически сохранены как UTC, без явного маркера).
 * JS `new Date("2026-04-29T08:35:07")` ИНТЕРПРЕТИРУЕТ naive как ЛОКАЛЬНОЕ
 * время, что даёт неверный сдвиг для пользователей вне UTC. Здесь мы
 * детектируем отсутствие tz-маркера и достраиваем `Z`, чтобы все
 * datetime-поля трактовались как абсолютные UTC моменты — что соответствует
 * договорённости с бэкендом (вся БД в UTC).
 */
export const parseBackendDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  const s = String(input);
  // Has Z, +HH:MM, -HH:MM, +HHMM, -HHMM at the end?
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
  return new Date(hasTz ? s : s + 'Z');
};

/**
 * Нормализация локали для избежания ошибок "Invalid language tag"
 * Преобразует en-US@posix в en-US, ru_RU в ru-RU и т.д.
 */
export const normalizeLocale = (locale) => {
  if (!locale) return 'en-US';
  
  return locale
    .replace(/_/g, '-')    // Заменяем подчеркивания на дефисы 
    .replace(/@.*$/, '')   // Удаляем @posix и другие суффиксы
    .trim();
};

/**
 * Безопасное получение локали браузера
 */
export const getSafeLocale = () => {
  try {
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    return normalizeLocale(locale);
  } catch {
    return 'en-US';
  }
};

/**
 * Безопасное форматирование даты
 */
export const formatDateSafe = (date, locale = 'ru-RU', options = {}) => {
  try {
    const normalizedLocale = normalizeLocale(locale);
    return parseBackendDate(date).toLocaleString(normalizedLocale, options);
  } catch (error) {
    console.warn('Date formatting error:', error);
    // Fallback к простому форматированию
    const d = parseBackendDate(date) || new Date();
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }
};

/**
 * Безопасное форматирование чисел
 */
export const formatNumberSafe = (number, locale = 'en-US', options = {}) => {
  try {
    const normalizedLocale = normalizeLocale(locale);
    return new Intl.NumberFormat(normalizedLocale, options).format(number);
  } catch (error) {
    console.warn('Number formatting error:', error);
    // Fallback к простому форматированию
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
};

/**
 * Форматирование валюты (доллары)
 */
export const formatCurrency = (amount, showSymbol = true) => {
  try {
    const formatted = formatNumberSafe(Math.abs(amount), 'en-US');
    return showSymbol ? `$${formatted}` : formatted;
  } catch {
    return showSymbol ? `$${amount}` : amount.toString();
  }
};

/**
 * Форматирование даты для русской локали в часовом поясе БРАУЗЕРА
 * [NOTIF-2026-05-22] Was: hardcoded UTC display ("12:00 UTC" suffix).
 * Теперь — browser local time, без timezone suffix, так чтобы сотрудники
 * из разных стран видели время по своим часам — синхронно с reminders/
 * `formatDateTime` из TimezoneContext.
 */
export const formatDateRu = (date, options = {}) => {
  try {
    const d = parseBackendDate(date);
    if (!d || isNaN(d.getTime())) return String(date);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return String(date);
  }
};

export default {
  normalizeLocale,
  getSafeLocale,
  formatDateSafe,
  formatNumberSafe,
  formatCurrency,
  formatDateRu
};